import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Calendar, LogOut, ArrowLeft, Inbox as InboxIcon, Loader2 } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gsd_admin";

const QuoteCard = ({ q }) => (
  <div data-testid={`inbox-quote-${q.id}`} className="card-depth rounded-xl border border-white/5 bg-coal p-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-heading text-xl uppercase tracking-wide text-offwhite">{q.name}</p>
        <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-warmgray">
          {new Date(q.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
        </p>
      </div>
      {q.preferred_date && (
        <span className="flex items-center gap-2 rounded-lg border gold-hairline px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gold">
          <Calendar size={13} /> {q.preferred_date}
        </span>
      )}
    </div>
    <div className="mt-5 grid gap-2.5 text-sm text-offwhite/80">
      <a href={`tel:${q.phone}`} data-testid={`inbox-phone-${q.id}`} className="flex items-center gap-2.5 transition-colors duration-300 hover:text-gold">
        <Phone size={14} className="text-gold" /> {q.phone}
      </a>
      <a href={`mailto:${q.email}`} data-testid={`inbox-email-${q.id}`} className="flex items-center gap-2.5 transition-colors duration-300 hover:text-gold">
        <Mail size={14} className="text-gold" /> {q.email}
      </a>
      <p className="flex items-center gap-2.5">
        <MapPin size={14} className="text-gold" /> {q.address}
      </p>
    </div>
    <p className="mt-4 border-t gold-hairline pt-4 text-sm leading-relaxed text-warmgray">{q.description}</p>
    {q.photo_data && (
      <img
        src={`data:${q.photo_type || "image/jpeg"};base64,${q.photo_data}`}
        alt={`Photo of the job submitted by ${q.name}`}
        data-testid={`inbox-photo-${q.id}`}
        className="mt-4 w-36 rounded-lg border gold-hairline"
      />
    )}
  </div>
);

const LoginForm = ({ onLogin }) => {
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      onLogin(data.token);
    } catch (err) {
      console.error("[inbox] login failed", err);
      setError("Wrong passcode. Try again.");
    }
  };

  return (
    <form onSubmit={submit} data-testid="inbox-login-form" className="card-depth mx-auto mt-24 max-w-sm rounded-xl border gold-hairline bg-coal p-8 text-center">
      <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">Owner Access</p>
      <h1 className="mt-3 font-heading text-3xl uppercase tracking-wide text-offwhite">Quote Inbox</h1>
      <input
        type="password"
        required
        value={passcode}
        onChange={(e) => setPasscode(e.target.value)}
        data-testid="inbox-passcode-input"
        placeholder="Enter passcode"
        className="mt-7 w-full rounded-lg border border-white/10 bg-ink px-4 py-3.5 text-center text-sm text-offwhite outline-none transition-colors duration-300 placeholder:text-warmgray/50 focus:border-gold"
      />
      {error && (
        <p data-testid="inbox-login-error" className="mt-3 text-xs font-semibold uppercase tracking-[0.15em] text-red-400">
          {error}
        </p>
      )}
      <button
        type="submit"
        data-testid="inbox-login-button"
        className="gold-glow-btn mt-5 w-full rounded-lg bg-gold px-6 py-3.5 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:bg-gold-light"
      >
        Open Inbox
      </button>
    </form>
  );
};

const QuoteList = ({ quotes, loading }) => {
  if (loading) {
    return (
      <p className="mt-16 flex items-center justify-center gap-3 text-warmgray">
        <Loader2 size={18} className="animate-spin text-gold" /> Loading requests...
      </p>
    );
  }
  if (quotes && quotes.length === 0) {
    return (
      <div data-testid="inbox-empty" className="mt-16 flex flex-col items-center gap-4 rounded-xl border border-dashed border-gold/40 bg-coal/50 p-14 text-center">
        <InboxIcon size={28} className="text-gold/70" />
        <p className="text-sm uppercase tracking-[0.25em] text-warmgray">No quote requests yet</p>
      </div>
    );
  }
  return <div className="mt-8 grid gap-5">{quotes && quotes.map((q) => <QuoteCard key={q.id} q={q} />)}</div>;
};

const useQuotes = (token, onUnauthorized) => {
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setQuotes(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!cancelled) setQuotes(data.quotes);
      })
      .catch((err) => {
        console.error("[inbox] failed to load quotes", err);
        if (!cancelled) onUnauthorized();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  return { quotes, loading };
};

export default function Inbox() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  const login = (newToken) => {
    sessionStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  };

  const { quotes, loading } = useQuotes(token, logout);

  return (
    <div data-testid="inbox-page" className="min-h-screen bg-ink px-5 py-10 md:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <a href="/" data-testid="inbox-back-link" aria-label="Back to site">
            <Wordmark className="h-8" testid="inbox-wordmark" />
          </a>
          {token && (
            <button
              onClick={logout}
              data-testid="inbox-logout-button"
              className="flex items-center gap-2 rounded-lg border gold-hairline px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              <LogOut size={14} /> Log Out
            </button>
          )}
        </div>

        {!token ? (
          <LoginForm onLogin={login} />
        ) : (
          <div className="mt-10">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">Owner Access</p>
                <h1 className="mt-2 font-heading text-4xl uppercase tracking-wide text-offwhite">Quote Inbox</h1>
              </div>
              <p data-testid="inbox-count" className="text-xs uppercase tracking-[0.25em] text-warmgray">
                {quotes ? `${quotes.length} request${quotes.length === 1 ? "" : "s"}` : ""}
              </p>
            </div>
            <QuoteList quotes={quotes} loading={loading} />
          </div>
        )}

        <a href="/" className="mt-12 flex w-fit items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-warmgray transition-colors duration-300 hover:text-gold">
          <ArrowLeft size={13} /> Back to site
        </a>
      </div>
    </div>
  );
}
